<template>
  <div class="audit-detail-page">
    <!-- 顶部标题栏 -->
    <el-card class="page-header-card">
      <div class="page-header">
        <span class="page-title">{{ $t('shopAudit.auditInfo') }}</span>
        <el-button type="warning" plain @click="goBack">
          <i class="el-icon-back"></i> {{ $t('shopAudit.back') }}
        </el-button>
      </div>
    </el-card>

    <!-- 店铺基本信息 -->
    <el-card class="section-card">
      <div slot="header" class="section-header">
        <i class="el-icon-shop section-icon"></i>
        <span>店铺基本信息</span>
      </div>
      <div class="section-body basic-info-body">
        <div class="shop-logo-wrap">
          <el-image :src="shopAuditDetailInfo.shopLogo" fit="cover" class="shop-logo">
            <div slot="error" class="image-placeholder">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>
          <div class="shop-logo-label">{{ $t('shopAudit.logo').replace('：', '') }}</div>
        </div>
        <div class="info-grid three-columns">
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.shopName') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.shopName) }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">{{ $t('shopAudit.shopDesc') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.shopDesc) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.shopType') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.shopTypeName) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.openType') }}</span>
            <span class="info-value">{{ formatValue(openTypeName(shopAuditDetailInfo.openType)) }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 商家信息 -->
    <el-card class="section-card">
      <div slot="header" class="section-header">
        <i class="el-icon-office-building section-icon"></i>
        <span>商家信息</span>
      </div>
      <div class="section-body">
        <div class="info-grid three-columns">
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.storeName') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.storeName) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.storeTel') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.storeTel) }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">{{ $t('shopAudit.storeAddress') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.storeAddress) }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 地址与联系信息 -->
    <el-card class="section-card">
      <div slot="header" class="section-header">
        <i class="el-icon-location-outline section-icon"></i>
        <span>地址与联系信息</span>
      </div>
      <div class="section-body">
        <div class="info-grid three-columns">
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.sendAddress') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.sendAddress) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.areaName') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.areaName) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.returnAddress') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.returnAddress) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.returnPerson') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.returnPerson) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.returnLink') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.returnLink) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.coordinatesX') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.mapX) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('shopAudit.coordinatesY') }}</span>
            <span class="info-value">{{ formatValue(shopAuditDetailInfo.mapY) }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 申请原因 -->
    <el-card class="section-card">
      <div slot="header" class="section-header">
        <i class="el-icon-document section-icon"></i>
        <span>申请原因</span>
      </div>
      <div class="section-body">
        <div class="info-grid">
          <div class="info-item full-width text-only">
            <span class="info-value reason-value">{{ formatValue(shopAuditDetailInfo.applyOpinion) }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 附件资料 -->
    <el-card class="section-card">
      <div slot="header" class="section-header">
        <i class="el-icon-paperclip section-icon"></i>
        <span>附件资料</span>
      </div>
      <div class="section-body">
        <div class="attachment-grid">
          <div v-for="field in attachmentFields" :key="field.key" class="attachment-item">
            <div class="attachment-title">{{ $t('shopAudit.' + field.key).replace('：', '') }}</div>
            <div class="attachment-content">
              <template v-if="shopAuditDetailInfo[field.key]">
                <span class="attachment-link" @click="previewFile(shopAuditDetailInfo[field.key])">
                  <i :class="field.icon"></i>
                  <span class="attachment-name">{{ getFileName(shopAuditDetailInfo[field.key]) }}</span>
                </span>
                <el-button type="primary" size="mini" icon="el-icon-download" @click.stop="downloadFile(shopAuditDetailInfo[field.key])">
                  下载
                </el-button>
              </template>
              <span v-else class="no-attachment">未上传</span>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 已审核意见 -->
    <el-card v-if="hasAudited" class="section-card audit-result-card">
      <div slot="header" class="section-header">
        <i class="el-icon-s-check section-icon"></i>
        <span>审核意见</span>
      </div>
      <div class="section-body">
        <div class="info-grid">
          <div class="info-item full-width text-only">
            <span class="info-value reason-value">{{ formatValue(shopAuditDetailInfo.auditOpinion) }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 审核操作：商品模块只查看，不显示审核表单 -->
    <el-card v-if="canAudit" class="section-card audit-action-card">
      <div slot="header" class="section-header">
        <i class="el-icon-edit-outline section-icon"></i>
        <span>{{ $t('shopAudit.auditResult') }}</span>
      </div>
      <div class="section-body">
        <el-form :model="auditInfo" label-width="120px" class="audit-form">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item :label="$t('shopAudit.auditStatus')">
                <el-select v-model="auditInfo.state" style="width: 100%;" :placeholder="$t('shopAudit.selectAuditStatus')">
                  <el-option :label="$t('shopAudit.agree')" value="1100"></el-option>
                  <el-option :label="$t('shopAudit.reject')" value="1200"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="24">
              <el-form-item :label="$t('shopAudit.auditOpinion')">
                <el-input type="textarea" :placeholder="$t('shopAudit.auditOpinionPlaceholder')"
                  v-model="auditInfo.auditOpinion" :rows="4"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item>
            <el-button type="primary" @click="auditSubmit">
              {{ $t('shopAudit.submit') }}
            </el-button>
            <el-button type="info" plain @click="refreshShopAuditManageInfo">
              {{ $t('shopAudit.cancel') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
    <!-- 图片预览弹窗 -->
    <el-dialog :visible.sync="previewVisible" title="附件预览" width="60%" :close-on-click-modal="true" center>
      <div style="text-align: center;">
        <el-image :src="previewImageUrl" fit="contain" style="max-width: 100%; max-height: 70vh;"></el-image>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryShopAudit, auditEditShop } from '@/api/shop/shopAuditApi'
import { queryNeedAuditShop, auditShop } from '@/api/admin/auditOpenShopApi'

export default {
  name: 'ShopAuditDetail',
  data() {
    return {
      from: '',
      shopAuditDetailInfo: {
        auditId: '',
        shopId: '',
        shopLogo: '',
        storeId: '',
        storeName: '',
        storeAddress: '',
        storeTel: '',
        shopName: '',
        shopDesc: '',
        mapX: '',
        mapY: '',
        sendAddress: '',
        returnAddress: '',
        shopTypeName: '',
        areaName: '',
        returnPerson: '',
        returnLink: '',
        state: '',
        shopType: '',
        openType: '',
        areaCode: '',
        auditOpinion: '',
        applyOpinion: '',
        statusCd: '',
        businessLicense: '',
        foodLicense: '',
        idCardFront: '',
        idCardBack: ''
      },
      auditInfo: {
        state: '',
        auditOpinion: ''
      },
      attachmentFields: [
        { key: 'businessLicense', icon: 'el-icon-document' },
        { key: 'foodLicense', icon: 'el-icon-document' },
        { key: 'idCardFront', icon: 'el-icon-picture-outline' },
        { key: 'idCardBack', icon: 'el-icon-picture-outline' }
      ],
      previewVisible: false,
      previewImageUrl: ''
    }
  },
  computed: {
    canAudit() {
      // 商品模块只查看，不审核
      if (this.from === 'goods') {
        return false
      }
      // 只有待审核状态才显示审核表单
      return this.shopAuditDetailInfo.state === '001' || this.shopAuditDetailInfo.state === '005'
    },
    hasAudited() {
      // 已审核（不通过/拒绝）时展示历史审核意见
      return this.shopAuditDetailInfo.state === '003'
    }
  },
  created() {
    this.from = this.$route.query.from || 'shop'
    this.shopAuditDetailInfo.auditId = this.$route.query.auditId
    this.shopAuditDetailInfo.shopId = this.$route.query.shopId
    this.shopAuditDetailInfo.storeId = this.$route.query.storeId
    this.loadShopAuditInfoData()
  },
  methods: {
    loadShopAuditInfoData() {
      // admin 入口使用 queryNeedAuditShop 查询店铺详情
      if (this.from === 'admin') {
        const params = {
          shopId: this.shopAuditDetailInfo.shopId,
          page: 0,
          row: 1
        }
        queryNeedAuditShop(params).then(res => {
          if (res.data && res.data.length > 0) {
            Object.assign(this.shopAuditDetailInfo, res.data[0])
            // 兼容 queryNeedAuditShop 返回的状态字段差异
            if (!this.shopAuditDetailInfo.applyOpinion) {
              this.shopAuditDetailInfo.applyOpinion = res.data[0].shopDesc || ''
            }
          }
        }).catch(error => {
          console.error('请求失败:', error)
        })
      } else {
        // shop / goods 入口沿用原 queryShopAudit
        const params = {
          page: 1,
          row: 1,
          auditId: this.shopAuditDetailInfo.auditId
        }
        queryShopAudit(params).then(res => {
          if (res.data && res.data.length > 0) {
            Object.assign(this.shopAuditDetailInfo, res.data[0])
          }
        }).catch(error => {
          console.error('请求失败:', error)
        })
      }
    },
    auditSubmit() {
      if (!this.auditValidate()) {
        this.$message.error(this.validateErrInfo)
        return
      }

      if (this.from === 'admin') {
        // 管理后台开店审核直接传审核动作码 1100/1200，
        // 后端 shop.auditShop 据此走状态机流转，避免错传 002/003 导致状态被重置为 001
        const params = {
          state: this.auditInfo.state,
          remark: this.auditInfo.auditOpinion,
          shopId: this.shopAuditDetailInfo.shopId,
          storeId: this.shopAuditDetailInfo.storeId
        }
        auditShop(params).then(res => {
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success('处理成功')
          // 审核完成即时刷新全局待办角标（开店审核）
          window.dispatchEvent(new Event('mall-audit-todo-changed'))
          this.goBack()
        }).catch(error => {
          this.$message.error(`处理失败:${error}`)
        })
      } else {
        const params = {
          state: this.auditInfo.state,
          auditOpinion: this.auditInfo.state === '1200'
            ? `拒绝:${this.auditInfo.auditOpinion}`
            : this.auditInfo.auditOpinion,
          shopId: this.shopAuditDetailInfo.shopId,
          storeId: this.shopAuditDetailInfo.storeId,
          auditId: this.shopAuditDetailInfo.auditId,
          remark: this.auditInfo.auditOpinion
        }
        auditEditShop(params).then(res => {
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success('处理成功')
          // 审核完成即时刷新全局待办角标（店铺变更）
          window.dispatchEvent(new Event('mall-audit-todo-changed'))
          this.goBack()
        }).catch(error => {
          this.$message.error(`处理失败:${error}`)
        })
      }
    },
    openTypeName(state) {
      const typeMap = {
        '1': this.$t('shopAudit.merchant'),
        '2': this.$t('shopAudit.service'),
        '3': this.$t('shopAudit.merchantAndService')
      }
      return typeMap[state] || ''
    },
    auditValidate() {
      if (!this.auditInfo.state) {
        this.validateErrInfo = this.$t('shopAudit.selectAuditStatus')
        return false
      }
      if (!this.auditInfo.auditOpinion) {
        this.validateErrInfo = this.$t('shopAudit.auditOpinionPlaceholder')
        return false
      }
      return true
    },
    goBack() {
      this.$router.go(-1)
    },
    refreshShopAuditManageInfo() {
      this.auditInfo = {
        state: '',
        auditOpinion: ''
      }
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return value
    },
    getFileName(url) {
      if (!url) return ''
      // 优先取最后一段路径作为文件名
      try {
        const pathname = new URL(url).pathname
        const decoded = decodeURIComponent(pathname)
        const parts = decoded.split('/')
        return parts[parts.length - 1] || '附件文件'
      } catch (e) {
        const decoded = decodeURIComponent(url)
        const parts = decoded.split('/')
        return parts[parts.length - 1] || '附件文件'
      }
    },
    getFileExt(url) {
      if (!url) return ''
      try {
        const pathname = new URL(url).pathname
        return pathname.split('.').pop().toLowerCase()
      } catch (e) {
        return url.split('?')[0].split('.').pop().toLowerCase()
      }
    },
    previewFile(url) {
      if (!url) {
        this.$message.warning('附件地址为空，无法预览')
        return
      }

      const ext = this.getFileExt(url)
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']

      if (imageExts.includes(ext)) {
        // 图片类型使用弹窗预览
        this.previewImageUrl = url
        this.previewVisible = true
      } else if (ext === 'pdf') {
        // PDF 类型在新标签页打开，浏览器通常内置预览
        window.open(url, '_blank')
      } else {
        this.$message.info('该类型文件不支持预览，请下载后查看')
      }
    },
    downloadFile(url) {
      if (!url) {
        this.$message.warning('附件地址为空，无法下载')
        return
      }
      const fileName = this.getFileName(url)
      const downloadUrl = `/mall/api/file/download?url=${encodeURIComponent(url)}`

      // 通过后端下载接口下载，避免跨域问题
      fetch(downloadUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`下载失败: ${response.status}`)
          }
          return response.blob()
        })
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
          this.$message.success('下载已开始')
        })
        .catch(error => {
          console.error('附件下载失败:', error)
          this.$message.error('附件下载失败，请检查网络或附件地址')
        })
    }
  }
}
</script>

<style scoped>
.audit-detail-page {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.page-header-card {
  margin-bottom: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.section-card {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.section-icon {
  margin-right: 8px;
  font-size: 18px;
  color: #409eff;
}

.section-body {
  padding: 10px 0;
}

/* 店铺基本信息：左侧 Logo + 右侧字段 */
.basic-info-body {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 24px;
}

.shop-logo-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 150px;
  flex-shrink: 0;
  padding: 10px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
}

.shop-logo {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
}

.shop-logo-label {
  margin-top: 10px;
  font-size: 14px;
  color: #606266;
  text-align: center;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  color: #909399;
  font-size: 28px;
}

/* 信息网格 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px 24px;
  width: 100%;
}

.info-grid.three-columns {
  grid-template-columns: repeat(3, 1fr);
}

.info-item {
  display: flex;
  align-items: stretch;
  line-height: 1.6;
  font-size: 14px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  min-height: 44px;
  transition: box-shadow 0.2s;
}

.info-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item.text-only {
  border: none;
  background: transparent;
}

.info-item.text-only .info-value {
  border: 1px solid #e4e7ed;
  border-left: 3px solid #409eff;
  border-radius: 6px;
  background: #fff;
  padding: 14px 16px;
}

.info-label {
  flex-shrink: 0;
  width: 110px;
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e4e7ed;
}

.info-label::after {
  content: '：';
  font-weight: normal;
  margin-left: 2px;
}

.info-value {
  flex: 1;
  color: #303133;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  word-break: break-all;
  background: #fff;
}

.reason-value {
  width: 100%;
  min-height: 80px;
  line-height: 1.8;
}

/* 附件区域 */
.attachment-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.attachment-item {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.attachment-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.attachment-title {
  width: 130px;
  flex-shrink: 0;
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  font-size: 14px;
  padding: 14px 12px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e4e7ed;
}

.attachment-content {
  flex: 1;
  padding: 14px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
}

.attachment-link {
  display: inline-flex;
  align-items: center;
  color: #409eff;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  max-width: calc(100% - 70px);
}

.attachment-link:hover {
  color: #66b1ff;
}

.attachment-link i {
  margin-right: 8px;
  font-size: 22px;
}

.attachment-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.no-attachment {
  color: #c0c4cc;
  font-size: 14px;
}

/* 审核操作 */
.audit-action-card ::v-deep .el-card__header {
  background: #f0f9ff;
}

.audit-result-card ::v-deep .el-card__header {
  background: #f4f4f5;
}

.audit-form {
  max-width: 900px;
}

/* 响应式：小屏下改为一列 */
@media screen and (max-width: 1200px) {
  .info-grid.three-columns {
    grid-template-columns: repeat(2, 1fr);
  }

  .attachment-grid {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 768px) {
  .info-grid.three-columns {
    grid-template-columns: 1fr;
  }

  .basic-info-body {
    flex-direction: column;
  }

  .shop-logo-wrap {
    margin: 0 auto;
  }
}
</style>
